// api/operations/taxiManifestApi.js

import axiosInstance from "../axiosInstance";

/**
 * Convierte un valor a entero positivo.
 * Lanza un error antes de enviar la solicitud cuando el valor es inválido.
 */
const toPositiveInteger = (value, fieldName) => {
    const parsedValue = Number(value);

    if (
        !Number.isInteger(parsedValue) ||
        parsedValue <= 0
    ) {
        throw new Error(`${fieldName} inválido.`);
    }

    return parsedValue;
};

const unwrapResponseData = (response) => {
    return (
        response?.data?.data ??
        response?.data ??
        response
    );
};

/**
 * Obtiene los equipos activos compatibles con el proyecto
 */
export const fetchTaxiTeams = async ({
    manifestId,
    serProId,
    active = true,
} = {}) => {
    const safeManifestId = toPositiveInteger(
        manifestId,
        "El identificador del manifiesto"
    );

    const safeSerProId = toPositiveInteger(
        serProId,
        "El identificador del servicio-proyecto"
    );

    const response = await axiosInstance.get(
        "/administration/taxi-service/taxi-teams",
        {
            params: {
                active: Boolean(active),
                manifestId: safeManifestId,
                serProId: safeSerProId,
            },
        }
    );

    const data = unwrapResponseData(response);

    return Array.isArray(data)
        ? data
        : [];
};

/**
 * Crea una persona, empleado/usuario, vehículo
 * y equipo conductor-vehículo en una sola operación.
 */
export const createIntegratedTaxiTeam = async (
    payload
) => {
    if (
        !payload ||
        typeof payload !== "object" ||
        Array.isArray(payload)
    ) {
        throw new Error(
            "Los datos del equipo son obligatorios."
        );
    }

    if (!payload.persona) {
        throw new Error(
            "Los datos de la persona son obligatorios."
        );
    }

    if (!payload.empleado) {
        throw new Error(
            "Los datos del empleado son obligatorios."
        );
    }

    if (!payload.vehiculo) {
        throw new Error(
            "Los datos del vehículo son obligatorios."
        );
    }

    if (!payload.equipo) {
        throw new Error(
            "Los datos del equipo son obligatorios."
        );
    }

    const safeSerProId = toPositiveInteger(
        payload.equipo.serProId,
        "El identificador del servicio-proyecto"
    );

    const normalizedPayload = {
        ...payload,

        empleado: {
            ...payload.empleado,

            /*rolid: toPositiveInteger(
                payload.empleado.rolid,
                "El rol del empleado"
            ),*/

            serviciosProyecto:
                Array.isArray(
                    payload.empleado.serviciosProyecto
                )
                    ? payload.empleado.serviciosProyecto.map(
                        (service) => ({
                            ...service,

                            serProId: toPositiveInteger(
                                service.serProId,
                                "El servicio-proyecto del empleado"
                            ),
                        })
                    )
                    : [],
        },

        vehiculo: {
            ...payload.vehiculo,

            orgid: toPositiveInteger(
                payload.vehiculo.orgid,
                "La organización del vehículo"
            ),

            serviciosProyecto:
                Array.isArray(
                    payload.vehiculo.serviciosProyecto
                )
                    ? payload.vehiculo.serviciosProyecto.map(
                        (service) => ({
                            ...service,

                            serProId: toPositiveInteger(
                                service.serProId,
                                "El servicio-proyecto del vehículo"
                            ),
                        })
                    )
                    : [],
        },

        equipo: {
            ...payload.equipo,
            serProId: safeSerProId,
        },
    };

    const response = await axiosInstance.post(
        "/administration/taxi-service/taxi-teams/integrated",
        normalizedPayload
    );

    return unwrapResponseData(response);
};

/**
 * Asigna un equipo registrado a un manifiesto.
 */
export const assignTaxiTeamToManifest = async ({ manifestId, equConVehId, liberar = false, }) => {

    const safeManifestId = toPositiveInteger(manifestId, "El identificador del manifiesto");
    const safeTeamId = toPositiveInteger(equConVehId, "El identificador del equipo");

    const response = await axiosInstance.put(
        `/administration/taxi-service/manifests/${safeManifestId}/taxi-assignment`,
        {
            equConVehId: safeTeamId,
            liberar: Boolean(liberar),
        }
    );

    return unwrapResponseData(response);
};

// Desasignar equipo del manifiesto (eliminar relación)
export const unassignTaxiTeamFromManifest = async ({ manifestId }) => {
    const safeManifestId = toPositiveInteger(manifestId, "El identificador del manifiesto");

    const response = await axiosInstance.delete(
        `/administration/taxi-service/manifests/${safeManifestId}/taxi-assignment`
    );

    return unwrapResponseData(response);
};

export const finishTaxiManifest = async ({ manifestId }) => {
    const safeManifestId = toPositiveInteger(manifestId, "El identificador del manifiesto");

    const response = await axiosInstance.put(
        `/administration/taxi-service/manifests/${safeManifestId}/finish`
    );

    return unwrapResponseData(response);
};